/**
 * AIMDv2 – Step 2: Learning Outcomes
 *
 * Dynamic list of outcome text inputs.
 * - First 3 outcomes are required (marked with *).
 * - Additional outcomes can be added via the + button.
 * - Any outcome beyond the first 3 can be removed via an X icon.
 * - Renumbering happens automatically based on array position.
 */

import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DosenInput from '../../../components/DosenInput';
import {
  OutcomesData,
  OutcomeItem,
  StepValidationResult
} from '../types';
import { MIN_REQUIRED_OUTCOMES, OUTCOME_MAX_LENGTH } from '../constants';

interface OutcomesStepProps {
  data: OutcomesData;
  validation: StepValidationResult;
  showErrors: boolean;
  onChange: (patch: Partial<OutcomesData>) => void;
}

const OutcomesStep: React.FC<OutcomesStepProps> = ({
  data,
  validation,
  showErrors,
  onChange
}) => {
  const { outcomes } = data;
  const errors = showErrors ? validation.errors : {};

  /** Update a single outcome's value by its id. */
  const handleOutcomeChange = (id: string, value: string) => {
    const updated = outcomes.map((o) => (o.id === id ? { ...o, value } : o));
    onChange({ outcomes: updated });
  };

  /** Add a new empty outcome at the end. */
  const handleAdd = () => {
    const newOutcome: OutcomeItem = {
      id: crypto.randomUUID(),
      value: ''
    };
    onChange({ outcomes: [...outcomes, newOutcome] });
  };

  /** Remove an outcome by id and renumber. */
  const handleRemove = (id: string) => {
    const filtered = outcomes.filter((o) => o.id !== id);
    onChange({ outcomes: filtered });
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 600,
        display: 'flex',
        flexDirection: 'column',
        gap: '28px'
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <Typography
          sx={{
            fontSize: 30,
            fontWeight: 500,
            fontFamily: 'Inter',
            color: 'var(--Text-text-primary, #FDFDFD)'
          }}
        >
          Goals
        </Typography>
        <Typography
          sx={{
            fontSize: 14,
            fontFamily: 'Inter',
            color: 'var(--Text-text-secondary, #B6B6B6)',
            lineHeight: '20px'
          }}
        >
          By completing this program, people should be able to:
        </Typography>
      </Box>

      {/* Global error (e.g. "At least 3 outcomes are required") */}
      {errors._global && (
        <Typography
          sx={{
            fontSize: 12,
            fontFamily: 'Inter',
            color: 'var(--UI-Error, #F44336)'
          }}
        >
          {errors._global}
        </Typography>
      )}

      {/* Outcome fields */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {outcomes.map((outcome, index) => {
          const isRequired = index < MIN_REQUIRED_OUTCOMES;
          const fieldKey = `outcome_${index}`;
          const fieldError = errors[fieldKey];

          return (
            <Box key={outcome.id} sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    fontFamily: 'Inter',
                    color: 'var(--Text-text-primary, #FDFDFD)',
                    lineHeight: '20px'
                  }}
                >
                  Outcome {index + 1}
                  {isRequired ? '*' : ''}
                </Typography>

                {/* Remove button – only for non-required outcomes */}
                {!isRequired && (
                  <IconButton
                    onClick={() => handleRemove(outcome.id)}
                    size="small"
                    sx={{
                      color: 'var(--Stroke-icon-dim, #868686)',
                      '&:hover': { color: 'var(--UI-Error, #F44336)' }
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>

              <DosenInput
                value={outcome.value}
                onChange={(e) => handleOutcomeChange(outcome.id, e.target.value)}
                placeholder={`Describe goal ${index + 1}...`}
                error={!!fieldError}
                helperText={fieldError}
                maxChars={OUTCOME_MAX_LENGTH}
              />
            </Box>
          );
        })}

        {/* Add outcome button */}
        <Box>
          <IconButton
            onClick={handleAdd}
            sx={{
              width: 32,
              height: 32,
              background: 'var(--Fill-Button-button-gray, #565656)',
              borderRadius: '16px',
              '&:hover': {
                background: 'var(--Fill-Button-button-gray, #565656)',
                opacity: 0.8
              }
            }}
          >
            <AddIcon sx={{ fontSize: 20, color: '#FDFDFD' }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default OutcomesStep;
